import React, { useState, useEffect } from 'react';
import { AskRequest } from '@hone-automation/common';
import { format } from 'date-fns';
import { prefixClass } from 'lib/utils';
import { Dropdown, IconButton } from 'presentation/components/KSQ2/Common';
import { DueDateBadge } from 'presentation/components/KSQ2/Common/DueDateBadge';
import { SimpleDatePicker } from './components/SimpleDatePicker';
import { usePatchQueueRequestMutation } from 'hooks/queue/';
import { useQueueContext } from 'presentation/components/Queue/QueueContext';
import { parseLocalDate } from 'presentation/components/KSQ2/Panels/BrowserPanel/utils/dateGrouping';
// import { DueDatePicker } from './components/DueDatePicker'; // Full picker for later
import './DueDateButton.scss';
import { IconNavCalendar } from 'components/Icons';

export interface DueDateButtonProps {
  /** The request to update */
  request: AskRequest;
}

/**
 * Due date button with date picker - patches request.dueDate
 */
export const DueDateButton: React.FC<DueDateButtonProps> = ({ request }) => {
  const prefix = prefixClass('conversation-header-duedate');
  const { dispatch, state } = useQueueContext();
  const { requests } = state;

  // Initialize from request.dueDate (parse as local date, not UTC)
  const initialDate = request.dueDate ? parseLocalDate(request.dueDate) : null;
  const [dueDate, setDueDate] = useState<Date | null>(initialDate);
  const [tempDate, setTempDate] = useState<Date | null>(initialDate);
  const [isOpen, setIsOpen] = useState(false);

  // Sync with request changes
  useEffect(() => {
    const newDate = request.dueDate ? parseLocalDate(request.dueDate) : null;
    setDueDate(newDate);
    setTempDate(newDate);
  }, [request.dueDate]);

  // Patch mutation for updating dueDate
  const patchMutation = usePatchQueueRequestMutation(data => {
    if (data) {
      // Update requests in context
      dispatch({
        type: 'SET_REQUESTS',
        payload: requests.map(req => (req.id === data.id ? data : req)),
      });
      // Update selected item
      dispatch({
        type: 'SET_SELECTED_ITEM',
        payload: data,
      });
    }
  });

  const handleDateChange = (date: Date | null) => {
    setTempDate(date);
  };

  const handleConfirm = () => {
    setDueDate(tempDate);
    // Format as YYYY-MM-DD for API
    const dueDateString = tempDate ? format(tempDate, 'yyyy-MM-dd') : null;
    patchMutation.mutate({ id: request.id, dueDate: dueDateString, locationId: request.locationId });
    setIsOpen(false);
  };

  const handleClear = () => {
    setDueDate(null);
    setTempDate(null);
    patchMutation.mutate({ id: request.id, dueDate: null, locationId: request.locationId });
    setIsOpen(false);
  };

  const handleBadgeClick = () => {
    setTempDate(dueDate);
    setIsOpen(true);
  };

  // Calculate status based on due date
  const getDueDateStatus = (): 'pending' | 'past-due' => {
    if (!dueDate) return 'pending';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    return due < today ? 'past-due' : 'pending';
  };

  const trigger = dueDate ? (
    <DueDateBadge
      date={dueDate}
      size="big"
      status={getDueDateStatus()}
      showClose={true}
      onClose={handleClear}
      onClick={handleBadgeClick}
    />
  ) : (
    <IconButton icon={<IconNavCalendar />} onClick={() => setIsOpen(true)} size="medium" tooltip={'Set Due Date'} />
  );

  return (
    <Dropdown
      trigger={trigger}
      position="bottom"
      alignment="start"
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      closeOnClickOutside={true}
      closeOnItemClick={false}
      enableSmartPosition={true}
    >
      <SimpleDatePicker
        selectedDate={tempDate}
        onDateChange={handleDateChange}
        onConfirm={handleConfirm}
        onClear={handleClear}
      />
    </Dropdown>
  );
};

export default DueDateButton;
